'use client';

import { Badge } from '@/components/ui/badge';
import { Icons } from '@/components/icons';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/cn';
import { truncateText } from '@/lib/truncate-text';
import {
  Fragment,
  forwardRef,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';

export type TData = Record<'v' | 'd', string | number>;

export type CustomBadge = (item: TData) => JSX.Element;

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  defaultValues: TData[];
  values?: TData[];
  onSelected?: (item: TData[]) => void;
  placeholder?: string;
  disabled?: boolean;
  customBadge?: CustomBadge;
  maxSelected?: number;
};

const MAX_BADGE_CHARACTERS = 32;
const BADGE_SPACING_CHARACTERS = 3;
const MAX_LENGTH = 8;

const allowMoreBadges = (selected: TData[]) => {
  let totalCharacters = 0;
  let allowedCount = 0;

  for (const item of selected) {
    const itemLength =
      truncateText(item.d ?? item.v, MAX_LENGTH).length +
      BADGE_SPACING_CHARACTERS;
    if (totalCharacters + itemLength <= MAX_BADGE_CHARACTERS) {
      totalCharacters += itemLength;
      allowedCount++;
    } else {
      break;
    }
  }

  return { allowedCount, totalCharacters };
};

const Badges = ({
  selected,
  customBadge,
  disabled,
}: {
  selected: TData[];
  customBadge?: CustomBadge;
  disabled?: boolean;
}) => {
  const { allowedCount } = allowMoreBadges(selected);
  return selected.slice(0, allowedCount).map((item, index) => (
    <Fragment key={`${item.v}-${index}`}>
      {customBadge ? (
        customBadge(item)
      ) : (
        <Badge
          key={`${item.v}-${index}`}
          variant='light-gray'
          className={cn(
            'max-h-6 rounded-full bg-subtle text-subtle-foreground transition-all',
            {
              'hover:bg-gray-200/80': !disabled,
              'cursor-not-allowed opacity-50': disabled,
            }
          )}
        >
          {truncateText(item.d ?? item.v, MAX_LENGTH)}
        </Badge>
      )}
    </Fragment>
  ));
};

export const MultiSelectInput = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      placeholder,
      onSelected,
      defaultValues,
      disabled,
      values,
      customBadge,
      maxSelected,
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);
    const inputBoxRef = useRef<HTMLDivElement>(null);

    const [inputValue, setInputValue] = useState('');

    const atMax =
      maxSelected !== undefined && (values?.length ?? 0) >= maxSelected;

    const handleSelect = (item: TData) => {
      if (atMax) return;
      onSelected?.([...(values ?? []), item]);
    };

    const handleUnselect = (item: TData) => {
      onSelected?.((values ?? []).filter((s) => s.v !== item.v));
    };

    const clearSelected = () => {
      onSelected?.([]);
    };

    const handleSelectAll = () => {
      onSelected?.(defaultValues);
    };

    const filteredItems = useMemo(() => {
      if (!inputValue) return defaultValues;

      return defaultValues.filter((item) =>
        item.d.toString().toLowerCase().includes(inputValue.toLowerCase())
      );
    }, [defaultValues, inputValue]);

    const { selectedItems, unselectedItems } = useMemo(() => {
      const selectedItems: TData[] = [];
      const unselectedItems: TData[] = [];

      for (const item of filteredItems) {
        if (values?.some((s) => s.v === item.v)) {
          selectedItems.push(item);
        } else {
          unselectedItems.push(item);
        }
      }

      return { selectedItems, unselectedItems };
    }, [filteredItems, values]);

    return (
      <Popover
        modal
        open={open}
        onOpenChange={(next) => !disabled && setOpen(next)}
      >
        <PopoverTrigger asChild>
          <div
            ref={inputBoxRef}
            role='button'
            aria-disabled={disabled}
            className='group flex max-h-64 min-h-11 w-full cursor-pointer items-center justify-center overflow-y-auto rounded-input border border-input px-4 py-2 text-sm transition-colors focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 hover:bg-accent'
          >
            <div className='flex w-full items-center justify-between gap-2 text-muted-foreground'>
              {(values ?? [])?.length > 0 ? (
                <div className='flex gap-2'>
                  <Badges
                    selected={values ?? []}
                    customBadge={customBadge}
                    disabled={disabled}
                  />
                </div>
              ) : (
                <span>{placeholder ?? 'Välj'}...</span>
              )}
              <div className='flex h-6 items-center gap-2'>
                {(values ?? [])?.length >
                  allowMoreBadges(values ?? []).allowedCount && (
                  <Badge
                    variant='light-gray'
                    className={cn(
                      'max-h-6 rounded-full bg-subtle text-subtle-foreground transition-all',
                      {
                        'hover:bg-gray-200/80': !disabled,
                        'cursor-not-allowed opacity-50': disabled,
                      }
                    )}
                  >
                    {'+' +
                      ((values ?? [])?.length -
                        allowMoreBadges(values ?? []).allowedCount)}
                  </Badge>
                )}

                <Icons.selectIndicator className='size-4' />
              </div>
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent
          className='p-0'
          style={{
            width: inputBoxRef.current?.offsetWidth,
          }}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <Command
            ref={ref}
            className='overflow-visible bg-transparent'
            shouldFilter={false}
          >
            <CommandInput
              disabled={disabled}
              ref={inputRef}
              value={inputValue}
              onValueChange={setInputValue}
              placeholder={'Sök...'}
            />
            <div className='flex items-center border-b'>
              {maxSelected === undefined && (
                <Button
                  variant={'ghost'}
                  className='w-1/2 font-medium'
                  size={'icon-sm'}
                  onClick={handleSelectAll}
                  disabled={values?.length === defaultValues.length}
                >
                  Välj alla
                </Button>
              )}
              <Button
                variant={'ghost'}
                className={cn(
                  'font-medium',
                  maxSelected === undefined ? 'w-1/2' : 'w-full'
                )}
                size={'icon-sm'}
                onClick={clearSelected}
                disabled={values?.length === 0}
              >
                Rensa alla
              </Button>
            </div>
            <CommandList>
              <CommandGroup className='h-full overflow-auto'>
                {/* Render selected items first */}
                {selectedItems.map((item) => (
                  <CommandItem
                    id={`item-${item.v}-selected`}
                    key={`${item.v}-${item.d}-selected`}
                    onSelect={() => {
                      setInputValue('');
                      handleUnselect(item);
                    }}
                    className='relative flex w-full cursor-default items-center rounded-sm py-1.5 pr-2 pl-8 text-sm'
                  >
                    <span className='absolute left-2 flex size-4 items-center justify-center'>
                      <Icons.check className='size-4' />
                    </span>
                    <span>{customBadge ? customBadge(item) : item.d}</span>
                  </CommandItem>
                ))}

                {/* Render unselected items at the bottom */}
                {unselectedItems.map((item) => (
                  <CommandItem
                    id={`item-${item.v}-unselected`}
                    key={`${item.v}-${item.d}-unselected`}
                    disabled={atMax}
                    onSelect={() => {
                      setInputValue('');
                      handleSelect(item);
                    }}
                    className='relative flex w-full cursor-default items-center rounded-sm py-1.5 pr-2 pl-8 text-sm hover:bg-accent data-[disabled=true]:opacity-50'
                  >
                    <span>{customBadge ? customBadge(item) : item.d}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

MultiSelectInput.displayName = 'MultiSelectInput';
